import { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { GetRevenueSummary } from "../services/revenueservice";
import { fetchdropdownMall, fetchdropdownTenant } from "../services/dropdown";
import { MONTHS_OPTIONS } from "../utils/index";
import { DropdownOption } from "../types/dropdown";

export interface SummaryRow {
  transactionDate: string;
  amount: number;
  totalData: number;
}

const currentYear = new Date().getFullYear();

export const INITIAL_MONTH = MONTHS_OPTIONS[new Date().getMonth()];
export const INITIAL_YEAR = String(currentYear);

export function useDailyTransaction() {
  const [mallOptions, setMallOptions] = useState<DropdownOption[]>([]);
  const [selectedMall, setSelectedMall] = useState("");
  const [tenantOptions, setTenantOptions] = useState<DropdownOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(INITIAL_MONTH);
  const [selectedYear, setSelectedYear] = useState(INITIAL_YEAR);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState<SummaryRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchdropdownMall(0);
        if (!res?.dropdownClassDatas) return;

        const options: DropdownOption[] = res.dropdownClassDatas.map(
          (m: { DataId: number; DataLabel: string }) => ({ value: m.DataId, label: m.DataLabel }),
        );
        setMallOptions(options);

        const stored =
          localStorage.getItem("mall_id") || sessionStorage.getItem("mall_id");
        if (stored && options.find((o) => o.value === Number(stored))) {
          setSelectedMall(stored);
        } else if (options.length === 1) {
          setSelectedMall(options[0].value.toString());
          localStorage.setItem("mall_id", options[0].value.toString());
        }
      } catch {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch mall options" });
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedMall) {
      setTenantOptions([]);
      setSelectedTenant("");
      setTableData([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetchdropdownTenant(parseInt(selectedMall, 10));
        if (!res?.dropdownClassDatas) return;

        const options: DropdownOption[] = res.dropdownClassDatas.map(
          (t: { DataId: number; DataLabel: string }) => ({ value: t.DataId, label: t.DataLabel }),
        );
        setTenantOptions(options);

        if (options.length === 1) {
          setSelectedTenant(options[0].value.toString());
          localStorage.setItem("tenant_id", options[0].value.toString());
        } else {
          const stored =
            localStorage.getItem("tenant_id") ||
            sessionStorage.getItem("tenant_id");
          if (stored && options.find((o) => o.value === Number(stored))) {
            setSelectedTenant(stored);
          } else {
            setSelectedTenant("");
            setTableData([]);
          }
        }
      } catch {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch tenant options" });
      }
    };
    load();
  }, [selectedMall]);

  useEffect(() => {
    if (!selectedTenant || !selectedMonth || !selectedYear) {
      setTableData([]);
      return;
    }
    const load = async () => {
      setIsLoading(true);
      try {
        const monthNumber = MONTHS_OPTIONS.indexOf(selectedMonth) + 1;
        const result = await GetRevenueSummary(
          parseInt(selectedTenant, 10),
          parseInt(selectedYear, 10),
          monthNumber,
        );
        if (!result) {
          setTableData([]);
          return;
        }

        const summaryList = (result as { summaryRevenueDatas?: Array<{ TransactionDate?: string; Amount?: number; TotalData?: number }> }).summaryRevenueDatas;
        const rows: SummaryRow[] = Array.isArray(summaryList)
          ? summaryList.map((item) => ({
              transactionDate: item.TransactionDate
                ? new Date(item.TransactionDate).toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "-",
              amount: item.Amount ?? 0,
              totalData: item.TotalData ?? 0,
            }))
          : [];
        setTableData(rows);
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch summary data",
        });
        setTableData([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [selectedTenant, selectedMonth, selectedYear]);

  const isMallDisabled = mallOptions.length === 1;
  const isTenantDisabled =
    !selectedMall || tenantOptions.length === 0 || tenantOptions.length === 1;

  const handleMallChange = (value: string) => {
    setSelectedMall(value);
    localStorage.setItem("mall_id", value);
    setSelectedTenant("");
    setTableData([]);
  };

  const handleTenantChange = (value: string) => {
    setSelectedTenant(value);
    localStorage.setItem("tenant_id", value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newPerPage: number, page: number) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };

  const filteredData = useMemo(
    () =>
      tableData.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      ),
    [tableData, searchQuery],
  );

  const totalAmount = useMemo(
    () => filteredData.reduce((sum, r) => sum + r.amount, 0),
    [filteredData],
  );

  const totalTransactions = useMemo(
    () => filteredData.reduce((sum, r) => sum + r.totalData, 0),
    [filteredData],
  );

  return {
    mallOptions,
    selectedMall,
    isMallDisabled,
    tenantOptions,
    selectedTenant,
    isTenantDisabled,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    searchQuery,
    setSearchQuery,
    handleMallChange,
    handleTenantChange,
    handlePageChange,
    handleRowsPerPageChange,
    filteredData,
    isLoading,
    totalAmount,
    totalTransactions,
    currentPage,
    rowsPerPage,
  };
}